// src/cockpit4/components/WorkspaceBizBar.tsx
// ============================================
// Phase 3 P0-3：各页顶部「一句话业务说明 + 跨模块跳转按钮」
//
// 定位：让用户清楚「这一页是干嘛的、下一步去哪」，引导沿业务主线走动，
// 而非在一堆菜单卡里猜业务顺序（对齐 UX需求文档 §三）。
//
// 视觉：与总控台网格/玻璃拟态一致的小密度横条 ——
//   左：渐变竖条 + 业务主链底色图标 + 一句话说明
//   右：跨模块跳转按钮（chip），点击 setWorkspace 跳转
// ============================================

import { ArrowRight } from "lucide-react";
import { useCockpit4Store, type WorkspaceId } from "../store";

export interface BizJump {
  label: string;
  to: WorkspaceId;
}

interface Props {
  /** 一句话业务说明 */
  desc: string;
  /** 省略号可选说明补充（如数据来源/去向），可选 */
  hint?: string;
  /** 跨模块跳转按钮 */
  jumps?: BizJump[];
  /** 强调色（默认青蓝） */
  accent?: string;
}

export default function WorkspaceBizBar({ desc, hint, jumps, accent = "#22D3EE" }: Props) {
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);

  return (
    <div className="relative flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-md px-3 py-2">
      {/* 左侧渐变竖条 */}
      <div
        className="w-[3px] self-stretch rounded-full flex-shrink-0"
        style={{ background: `linear-gradient(180deg, ${accent}, ${accent}00)` }}
      />
      {/* 说明 */}
      <div className="flex-1 min-w-0 leading-snug">
        <span className="text-[11px] text-gray-200">{desc}</span>
        {hint && (
          <span className="ml-1.5 text-[10px] font-mono text-gray-500">{hint}</span>
        )}
      </div>
      {/* 跳转按钮 */}
      {jumps && jumps.length > 0 && (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {jumps.map((j) => (
            <button
              key={`${j.to}-${j.label}`}
              onClick={() => setWorkspace(j.to)}
              className="flex items-center gap-1 rounded-md border border-white/[0.08] bg-white/[0.03] px-2 py-1 text-[10px] font-mono text-gray-300 hover:text-white hover:border-white/[0.2] hover:bg-white/[0.06] transition-colors"
            >
              {j.label}
              <ArrowRight className="w-3 h-3" style={{ color: accent }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}